/**
 * Theme Components — Shoppable Video Carousel
 * Web Component: <shoppable-video-carousel>
 *
 * Gerencia o modal fullscreen com carousel de videos,
 * barras de progresso segmentadas, navegacao por tap/swipe/teclado,
 * e exibe o card do produto correspondente ao video ativo.
 * Construído com base exata em video-stories.js.
 */
if (!customElements.get('shoppable-video-carousel')) {
  class ShoppableVideoCarousel extends HTMLElement {
    constructor() {
      super();
      this._triggers = [];
      this._raf = null;
      this._isMuted = true;
      this._carousel = null;
      this._swiper = null;
      this._modalEl = null;
      this._modalDiv = null;
      this._stage = null;
      this._videos = [];
      this._progressSegments = [];
      this._activeIndex = -1;
      this._productCard = null;

      this._onCarouselInit = this._onCarouselInit.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
    }

    connectedCallback() {
      this._blockId = this.dataset.blockId || 'default';
      this._modalId = 'shoppable-modal-' + this._blockId;
      this._triggers = [...this.querySelectorAll('.shoppable-video__video-wrapper')];
      this._items = [...this.querySelectorAll('.shoppable-video__item')];

      // Move modal to document.body to escape section stacking contexts
      this._modalEl = this.querySelector('modal-component');
      if (this._modalEl) {
        document.body.appendChild(this._modalEl);
      }

      // Cache elements inside modal (now in body)
      this._modalDiv = this._modalEl?.querySelector('.modal');
      this._stage = this._modalEl?.querySelector('.video-stories__stage');
      this._carousel = this._modalEl?.querySelector('carousel-component');
      this._progressSegments = [...(this._modalEl?.querySelectorAll('.video-stories__progress-segment') || [])];
      this._productCard = this._modalEl?.querySelector('.shoppable-video-modal__product');

      this._bindThumbnails();
      this._bindModalControls();
      this._initCarousel();
    }

    disconnectedCallback() {
      this._stopProgressLoop();
      document.removeEventListener('keydown', this._onKeyDown);
      if (this._modalEl?.parentNode === document.body) {
        document.body.removeChild(this._modalEl);
      }
    }

    /* ----------------------------------------------------------------
       Carousel init
       ---------------------------------------------------------------- */

    _initCarousel() {
      if (!this._carousel) return;

      // Swiper may already be initialized (CDN cached)
      if (this._carousel.swiper) {
        this._onCarouselReady(this._carousel.swiper);
      }
      this._carousel.addEventListener('carousel:init', this._onCarouselInit);
    }

    _onCarouselInit(e) {
      if (e.detail?.swiper && !this._swiper) {
        this._onCarouselReady(e.detail.swiper);
      }
    }

    _onCarouselReady(swiper) {
      this._swiper = swiper;
      this._cacheVideos();

      this._swiper.on('slideChange', () => {
        this._activateSlide(this._swiper.activeIndex);
      });
    }

    _cacheVideos() {
      this._ensureModalVideos();
      this._videos = [...this._modalEl.querySelectorAll('.video-stories__video')];
    }

    // Os slides do modal são renderizados vazios no Liquid (o bloco-pai não
    // consegue ler a fonte do vídeo dos filhos). Aqui criamos cada <video> a
    // partir do data-video-src do item inline correspondente, na mesma ordem.
    _ensureModalVideos() {
      const slides = [...this._modalEl.querySelectorAll('.video-stories__slide')];
      slides.forEach((slide, i) => {
        if (slide.querySelector('.video-stories__video')) return;

        const src = this._items[i]?.dataset.videoSrc;
        if (!src) return;

        const video = document.createElement('video');
        video.className = 'video-stories__video';
        video.muted = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.preload = 'none';
        video.setAttribute('aria-label', `Vídeo ${i + 1} de ${slides.length}`);

        const source = document.createElement('source');
        source.src = src;
        source.type = 'video/mp4';
        video.appendChild(source);

        slide.appendChild(video);
      });
    }

    /* ----------------------------------------------------------------
       Thumbnails
       ---------------------------------------------------------------- */

    _bindThumbnails() {
      this._triggers.forEach((trigger, index) => {
        trigger.addEventListener('click', (e) => {
          // Prevent opening modal if clicking play/mute buttons
          if (e.target.closest('.shoppable-video__play-btn') || e.target.closest('.shoppable-video__mute-btn')) return;
          this._openStory(index);
        });
      });
    }

    _openStory(index) {
      this._pendingIndex = index;

      if (this._modalDiv) {
        this._modalDiv.classList.add('modal--active');
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this._onKeyDown);

        this._modalEl.dispatchEvent(
          new CustomEvent('modal:open', {
            bubbles: true,
            detail: { id: this._modalId, type: 'custom' },
          })
        );

        // Pause all inline videos
        this._items.forEach((item) => {
          const v = item.querySelector('video');
          if (v) v.pause();
        });

        this._onModalOpened();
      }
    }

    /* ----------------------------------------------------------------
       Modal open / close
       ---------------------------------------------------------------- */

    _onModalOpened() {
      const index = this._pendingIndex || 0;
      this._pendingIndex = null;

      const initSlides = () => {
        this._cacheVideos();

        if (this._swiper) {
          this._swiper.update();
          this._swiper.slideTo(index, 0);
          this._swiper.update();
        }

        this._activateSlide(index);
      };

      const onTransitionEnd = (e) => {
        if (e.target !== this._modalDiv) return;
        this._modalDiv.removeEventListener('transitionend', onTransitionEnd);
        initSlides();
      };

      this._modalDiv.addEventListener('transitionend', onTransitionEnd);

      setTimeout(() => {
        this._modalDiv.removeEventListener('transitionend', onTransitionEnd);
        initSlides();
      }, 350);
    }

    _closeModal() {
      this._deactivateCurrentSlide();
      this._resetAllProgress();
      this._activeIndex = -1;

      document.removeEventListener('keydown', this._onKeyDown);

      if (this._modalDiv) {
        this._modalDiv.classList.remove('modal--active');
        document.body.style.overflow = '';

        this._modalEl.dispatchEvent(
          new CustomEvent('modal:close', {
            bubbles: true,
            detail: { id: this._modalId, type: 'custom' },
          })
        );
      }

      // Resume inline videos that are visible
      this._items.forEach((item) => {
        const v = item.querySelector('video');
        if (v) {
          v.play().then(() => {
            item.classList.add('is-playing');
          }).catch(() => {});
        }
      });
    }

    /* ----------------------------------------------------------------
       Slide activation / video control
       ---------------------------------------------------------------- */

    _activateSlide(index) {
      if (index === this._activeIndex) return;

      this._deactivateCurrentSlide();
      this._activeIndex = index;

      const video = this._videos[index];
      if (!video) return;

      // Update progress bars
      this._progressSegments.forEach((seg, i) => {
        const fill = seg.querySelector('.video-stories__progress-fill');
        if (i < index) {
          seg.classList.add('is-complete');
          fill.style.width = '100%';
        } else {
          seg.classList.remove('is-complete');
          fill.style.width = '0%';
        }
      });

      video.muted = this._isMuted;
      video.currentTime = 0;

      const tryPlay = () => {
        video.play().catch(() => {
          video.muted = true;
          this._isMuted = true;
          this._syncMuteUI();
          video.play().catch(() => {});
        });
      };

      if (video.readyState >= 2) {
        tryPlay();
      } else {
        video.load();
        video.addEventListener('canplay', tryPlay, { once: true });
      }

      this._startProgressLoop();
      this._updateProductCard(index);

      // Auto-advance on video end
      video._onEnded = () => this._onVideoEnded();
      video.addEventListener('ended', video._onEnded);
    }

    _deactivateCurrentSlide() {
      if (this._activeIndex < 0) return;

      const video = this._videos[this._activeIndex];
      if (video) {
        video.pause();
        if (video._onEnded) {
          video.removeEventListener('ended', video._onEnded);
          video._onEnded = null;
        }
      }

      this._stopProgressLoop();
    }

    _onVideoEnded() {
      const nextIndex = this._activeIndex + 1;
      if (nextIndex < this._videos.length) {
        if (this._swiper) this._swiper.slideNext();
      } else {
        this._closeModal();
      }
    }

    /* ----------------------------------------------------------------
       Progress bars
       ---------------------------------------------------------------- */

    _startProgressLoop() {
      this._stopProgressLoop();

      const update = () => {
        const video = this._videos[this._activeIndex];
        if (!video || video.paused) {
          this._raf = requestAnimationFrame(update);
          return;
        }

        const pct = (video.currentTime / video.duration) * 100 || 0;
        const segment = this._progressSegments[this._activeIndex];
        if (segment) {
          segment.querySelector('.video-stories__progress-fill').style.width = pct + '%';
        }

        this._raf = requestAnimationFrame(update);
      };

      this._raf = requestAnimationFrame(update);
    }

    _stopProgressLoop() {
      if (this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = null;
      }
    }

    _resetAllProgress() {
      this._progressSegments.forEach((seg) => {
        seg.classList.remove('is-complete');
        seg.querySelector('.video-stories__progress-fill').style.width = '0%';
      });
    }

    /* ----------------------------------------------------------------
       Product card overlay
       ---------------------------------------------------------------- */

    _updateProductCard(index) {
      if (!this._productCard) return;

      const item = this._items[index];
      if (!item) return;

      const url = item.dataset.productUrl;
      const title = item.dataset.productTitle;
      const price = item.dataset.productPrice;
      const image = item.dataset.productImage;
      const imageAlt = item.dataset.productImageAlt;

      if (!url) {
        this._productCard.style.display = 'none';
        return;
      }

      this._productCard.href = url;
      this._productCard.querySelector('.shoppable-video-modal__product-title').textContent = title || '';
      this._productCard.querySelector('.shoppable-video-modal__product-price').textContent = price || '';

      const imgEl = this._productCard.querySelector('.shoppable-video-modal__product-img');
      if (image) {
        imgEl.src = image;
        imgEl.alt = imageAlt || title || '';
        imgEl.style.display = '';
      } else {
        imgEl.style.display = 'none';
      }

      this._productCard.style.display = '';
    }

    /* ----------------------------------------------------------------
       Modal controls
       ---------------------------------------------------------------- */

    _bindModalControls() {
      // Close button
      const closeBtn = this._modalEl?.querySelector('.video-stories__close');
      if (closeBtn) closeBtn.addEventListener('click', () => this._closeModal());

      // Overlay close
      const overlay = this._modalEl?.querySelector('.modal__overlay');
      if (overlay) overlay.addEventListener('click', () => this._closeModal());

      // Mute toggle
      const muteBtn = this._modalEl?.querySelector('.video-stories__mute');
      if (muteBtn) muteBtn.addEventListener('click', () => this._toggleMute());

      // Tap zones
      const tapPrev = this._modalEl?.querySelector('[data-tap="prev"]');
      const tapNext = this._modalEl?.querySelector('[data-tap="next"]');
      if (tapPrev) tapPrev.addEventListener('click', () => this._goPrev());
      if (tapNext) tapNext.addEventListener('click', () => this._goNext());
    }

    _toggleMute() {
      this._isMuted = !this._isMuted;
      const video = this._videos[this._activeIndex];
      if (video) video.muted = this._isMuted;
      this._syncMuteUI();
    }

    _syncMuteUI() {
      if (this._stage) {
        this._stage.classList.toggle('is-unmuted', !this._isMuted);
      }
    }

    _goPrev() {
      if (this._activeIndex <= 0) return;
      if (this._swiper) this._swiper.slidePrev();
    }

    _goNext() {
      if (this._activeIndex >= this._videos.length - 1) {
        this._closeModal();
        return;
      }
      if (this._swiper) this._swiper.slideNext();
    }

    /* ----------------------------------------------------------------
       Keyboard
       ---------------------------------------------------------------- */

    _onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this._closeModal();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (this._swiper) this._swiper.slidePrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this._goNext();
      } else if (e.key === 'm') {
        e.preventDefault();
        this._toggleMute();
      }
    }
  }

  customElements.define('shoppable-video-carousel', ShoppableVideoCarousel);
}
